import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useStorage } from '../hooks/useStorage';
import SafeIcon from '../common/SafeIcon';
import ImageWithFallback from './ImageWithFallback';
import supabase from '../lib/supabase';
import * as FiIcons from 'react-icons/fi';

const { FiUpload, FiCheck, FiX, FiImage, FiTrash2, FiRefreshCw } = FiIcons;

/**
 * ImagePicker component for selecting or uploading images
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Currently selected image URL
 * @param {function} props.onChange - Callback when image is selected
 * @param {string} props.folder - Folder to upload/browse images (default: 'general')
 * @param {string} props.bucket - Supabase storage bucket (default: 'team_logos')
 * @param {boolean} props.allowUpload - Allow uploading new images (default: true)
 * @param {string} props.aspectRatio - CSS aspect ratio class (default: 'aspect-video')
 */
const ImagePicker = ({ 
  value, 
  onChange, 
  folder = 'general', 
  bucket = 'team_logos',
  allowUpload = true,
  aspectRatio = 'aspect-video'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(value);
  
  const { uploadFile, uploading: storageUploading, error: uploadError } = useStorage();

  useEffect(() => {
    if (isOpen) {
      fetchImages();
    }
  }, [isOpen, folder, bucket]);

  useEffect(() => {
    setSelectedImage(value);
  }, [value]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      
      // List files in the specified folder
      const { data, error } = await supabase.storage
        .from(bucket)
        .list(folder, {
          limit: 50,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) throw error;

      // Get public URLs for all images
      const imageUrls = data
        .filter(file => {
          const ext = file.name.toLowerCase();
          return ext.endsWith('.jpg') || ext.endsWith('.jpeg') || 
                 ext.endsWith('.png') || ext.endsWith('.gif') || 
                 ext.endsWith('.webp');
        })
        .map(file => {
          const { data: { publicUrl } } = supabase.storage
            .from(bucket)
            .getPublicUrl(`${folder}/${file.name}`);
          
          return {
            name: file.name,
            url: publicUrl,
            created_at: file.created_at,
            size: file.metadata?.size
          };
        });

      setImages(imageUrls);
    } catch (err) {
      console.error('Error fetching images:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Παρακαλώ ανεβάστε μια έγκυρη εικόνα (JPG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('Το αρχείο είναι πολύ μεγάλο. Παρακαλώ ανεβάστε αρχείο μικρότερο από 5MB.');
      return;
    }

    try {
      setUploading(true);
      const uploadedUrl = await uploadFile(file, bucket, `${folder}/`);
      
      if (uploadedUrl) {
        // Refresh the images list
        await fetchImages();
        // Auto-select the newly uploaded image
        setSelectedImage(uploadedUrl);
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      alert('Σφάλμα κατά τη μεταφόρτωση της εικόνας');
    } finally {
      setUploading(false);
    }
  };

  const handleImageSelect = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const handleConfirm = () => {
    onChange(selectedImage);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setSelectedImage(value);
    setIsOpen(false);
  };

  const handleDeleteImage = async (imageUrl, imageName) => {
    if (!confirm('Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτή την εικόνα;')) return;

    try {
      const filePath = `${folder}/${imageName}`;
      const { error } = await supabase.storage
        .from(bucket)
        .remove([filePath]);

      if (error) throw error;

      // If the deleted image was selected, clear the selection
      if (selectedImage === imageUrl) {
        setSelectedImage('');
      }

      // Refresh the images list
      await fetchImages();
    } catch (err) {
      console.error('Error deleting image:', err);
      alert('Σφάλμα κατά τη διαγραφή της εικόνας');
    }
  };

  return (
    <div className="relative">
      {/* Current Image Display */}
      <div className={`${aspectRatio} bg-gray-100 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 hover:border-primary-400 transition-colors cursor-pointer`}
           onClick={() => setIsOpen(true)}>
        {value ? (
          <ImageWithFallback
            src={value}
            alt="Selected image"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <div className="text-center">
              <SafeIcon icon={FiImage} className="w-12 h-12 mx-auto mb-2" />
              <p className="text-sm">Κάντε κλικ για επιλογή εικόνας</p>
            </div>
          </div>
        )}
      </div>

      {/* Image Picker Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Επιλογή Εικόνας</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={fetchImages}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
                    title="Ανανέωση"
                  >
                    <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleCancel}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Upload Section */}
              {allowUpload && (
                <div className="mt-4">
                  <label className="cursor-pointer bg-primary-50 hover:bg-primary-100 border-2 border-dashed border-primary-300 rounded-lg p-4 flex items-center justify-center transition-colors">
                    <SafeIcon icon={FiUpload} className="w-5 h-5 mr-2 text-primary-600" />
                    <span className="text-primary-600 font-medium">
                      {uploading || storageUploading ? 'Μεταφόρτωση...' : 'Ανεβάστε νέα εικόνα'}
                    </span>
                    <input
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      onChange={handleFileUpload}
                      disabled={uploading || storageUploading}
                    />
                  </label>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-96">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
                  <span className="ml-3 text-gray-600">Φόρτωση εικόνων...</span>
                </div>
              ) : images.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {images.map((image, index) => (
                    <div
                      key={image.url}
                      className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                        selectedImage === image.url
                          ? 'border-primary-500 ring-2 ring-primary-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleImageSelect(image.url)}
                    >
                      <div className="aspect-video">
                        <ImageWithFallback
                          src={image.url}
                          alt={image.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Selection Indicator */}
                      {selectedImage === image.url && (
                        <div className="absolute inset-0 bg-primary-500 bg-opacity-20 flex items-center justify-center">
                          <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                            <SafeIcon icon={FiCheck} className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      )}

                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteImage(image.url, image.name);
                        }}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                        title="Διαγραφή εικόνας"
                      >
                        <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                      </button>

                      {/* Image Info */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <p className="text-xs truncate">{image.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <SafeIcon icon={FiImage} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Δεν υπάρχουν διαθέσιμες εικόνες</p>
                  {allowUpload && (
                    <p className="text-gray-400 text-sm mt-2">Ανεβάστε την πρώτη σας εικόνα παραπάνω</p>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Ακύρωση
              </button>
              <button
                onClick={handleConfirm}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors"
              >
                Επιλογή
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ImagePicker;